import type { ReferencePhoto } from '../references/references-api';
import type { PhotoKey, StepsState, StepState } from './steps-api';

const photoKey = (jobId: number, frame: number): string => `${jobId}:${frame}`;

// the server names a photo by its place in its own list; the panel and the strip by the strip's
export function toStripState(state: StepsState, strip: ReferencePhoto[]): StepsState {
    const stripIndex = new Map(strip.map((photo) => [photoKey(photo.jobId, photo.frame), photo.index]));
    const toStrip = new Map<number, number>();
    (state.photos ?? []).forEach((photo, position) => {
        const index = stripIndex.get(photoKey(photo.job, photo.frame));
        if (index !== undefined) toStrip.set(position, index);
    });
    const rename = (positions?: number[]): number[] => (positions ?? [])
        .filter((position) => toStrip.has(position))
        .map((position) => toStrip.get(position) as number)
        .sort((a, b) => a - b);
    const frames = rename(state.frames);
    const steps: StepState[] = state.steps.map((step) => ({
        ...step,
        counts: { ...step.counts, total: frames.length },
        done_frames: rename(step.done_frames),
        running_frames: rename(step.running_frames),
        queued_frames: rename(step.queued_frames),
        failures: (step.failures ?? [])
            .filter((failure) => toStrip.has(failure.frame))
            .map((failure) => ({ ...failure, frame: toStrip.get(failure.frame) as number })),
    }));
    const frameStatus: StepsState['frame_status'] = {};
    for (const [position, index] of toStrip) {
        const status = state.frame_status[String(position)];
        if (status) frameStatus[String(index)] = status;
    }
    return {
        ...state, frames, steps, frame_status: frameStatus,
    };
}

export function stripPhotoKeys(indexes: number[], strip: ReferencePhoto[]): PhotoKey[] {
    return indexes.map((index) => {
        const photo = strip[index];
        // the open photo is unknown while its job loads: never fall back to running on all photos
        if (!photo) throw new Error('This photo is not loaded yet. Try again in a moment.');
        return { reference: photo.referenceId, frame: photo.frame };
    });
}
